import * as React from "react";
import styled from "styled-components";

import { VariableSizeGrid, GridChildComponentProps } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";

import { select } from "@hubper/theme";

import { IconButton, Button } from "@hubper/core-ui/elements";

import { LanguageMutation } from "../../../../api/lang";

import useAddNamespace from "../AddNamespace";
import useAddEntry from "../AddEntry";

import { useUser } from "../../../../UserProvider";

/**
 * Styles
 */

const COL_WIDTH = 350;
const UTIL_COL_WIDTH = 50;

const Base = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  width: calc(100vw - 500px);
`;

const Top = styled.div`
  border-bottom: 1px solid ${select.color(["grey", 0])};
  display: flex;
  overflow: hidden;
`;

const KeyCol = styled.div`
  box-sizing: border-box;
  padding: 12px 16px;
  background-color: ${select.color(["grey", 0])};

  font-family: monospace;
  font-size: 16px;

  &:focus-within {
    opacity: 1 !important;
    filter: none !important;
  }
`;

const NSCol = styled.div`
  background-color: ${select.color(["grey", 3])};
  font-weight: 700;
  padding: 12px 16px;
  box-sizing: border-box;

  &:focus-within {
    opacity: 1 !important;
    filter: none !important;
  }
`;

const UtilCol = styled.div`
  padding: 4px;
`;

const Col = styled.div`
  box-sizing: border-box;
  &:focus-within {
    opacity: 1 !important;
    filter: none !important;
  }

  &:hover:not(:focus-within) {
    background-color: ${select.color(["primary", 1])};
  }
`;

const Body = styled.div`
  flex: 1 1 0;
  overflow: auto;

  &:focus-within ${Col}, &:focus-within ${KeyCol} {
    opacity: 0.3;
    filter: blur(1px);
  }
`;

const HeaderCol = styled.div`
  width: ${COL_WIDTH}px;
  min-width: ${COL_WIDTH}px;
  min-height: 1px;
  padding: 12px 16px;
  font-weight: bold;
  box-sizing: border-box;
`;

const TextInput = styled.textarea`
  resize: none;
  width: 100%;
  height: 100%;
  background-color: transparent;
  appearance: none;
  border: 0;
  padding: 12px 16px;
  border-radius: 3px;
  box-sizing: border-box;
`;

const FieldInput = styled.input`
  resize: none;
  width: 100%;
  height: 100%;
  background-color: transparent;
  appearance: none;
  border: 0;
  padding: 12px 16px;
  border-radius: 3px;
  box-sizing: border-box;
  font-family: inherit;
  font-weight: inherit;
`;

/**
 * Sub-Components
 */

type CellProps = {
  value: string;
  style: React.CSSProperties;
  showOnlyEmpty: boolean;
  onChange: (value: string) => void;
  onBlur: (value: string) => void;
  onSave: (value: string) => void;
  onFocus: () => void;
  readOnly: boolean;
};

function Cell({
  value,
  style,
  showOnlyEmpty,
  onChange,
  onBlur,
  onFocus,
  onSave,
  readOnly,
}: CellProps) {
  const input = React.useRef<null | HTMLTextAreaElement>(null);

  const lastValue = React.useRef(value);

  return (
    <Col
      style={{
        ...style,
        border:
          showOnlyEmpty && (!value || value.indexOf("@todo@") > -1)
            ? "1px solid red"
            : "0",
      }}
    >
      <TextInput
        readOnly={readOnly}
        ref={input}
        value={value}
        onChange={(evt: any) => onChange(evt.target.value)}
        onBlur={(evt: any) => {
          if (value !== lastValue.current) {
            onSave(value);
          }
          lastValue.current = value;
          onBlur(evt.target.value);
        }}
        onFocus={() => {
          lastValue.current = value;
          onFocus();
        }}
      />
    </Col>
  );
}

type NsCellProps = {
  onChange: (value: string) => void;
  value: string;
  style: React.CSSProperties;
  readOnly: boolean;
};

function NsCell({
  value: externalValue,
  onChange,
  style,
  readOnly,
}: NsCellProps) {
  const [value, setValue] = React.useState(externalValue);

  const lastValue = React.useRef(externalValue);

  React.useEffect(() => {
    setValue(externalValue);
    lastValue.current = externalValue;
  }, [externalValue]);

  return (
    <NSCol
      style={{
        ...style,
        display: "flex",
        alignItems: "center",
      }}
    >
      <FieldInput
        readOnly={readOnly}
        value={value}
        onBlur={() => {
          if (value.length > 0) {
            onChange(value);
          } else {
            setValue(lastValue.current);
          }
        }}
        onChange={(evt: any) => {
          setValue(evt.target.value);
        }}
      />
    </NSCol>
  );
}

type KeyCellProps = {
  onChange: (value: string) => void;
  value: string;
  style: React.CSSProperties;
  readOnly: boolean;
};

function KeyCell({
  value: externalValue,
  onChange,
  style,
  readOnly,
}: KeyCellProps) {
  const [value, setValue] = React.useState(externalValue);

  const lastValue = React.useRef(externalValue);

  React.useEffect(() => {
    setValue(externalValue);
    lastValue.current = externalValue;
  }, [externalValue]);

  return (
    <KeyCol style={style}>
      <FieldInput
        readOnly={readOnly}
        value={value}
        onBlur={() => {
          if (value === lastValue.current) {
            return;
          }

          if (value.length > 0) {
            onChange(value);
          } else {
            setValue(lastValue.current);
          }
        }}
        onChange={(evt: any) => {
          setValue(evt.target.value);
        }}
      />
    </KeyCol>
  );
}

/**
 * Main
 */

type FlattenedRow = {
  isNS: boolean;
  isUtil: boolean;
  ns: string;
  key?: string;
  values?: { language: string; text: string }[];
};

function flattenTranslations(translations: any, referenceLanguage: string) {
  const baseLang = translations[referenceLanguage];
  const languages = Object.keys(translations);

  return Object.keys(baseLang).reduce((list, ns) => {
    return [
      ...list,
      { isNS: true, ns, isUtil: false },
      ...Object.keys(baseLang[ns]).reduce((list, key) => {
        return [
          ...list,
          {
            isNS: false,
            isUtil: false,
            key,
            ns,
            values: languages.map((language) => ({
              language,
              text: translations[language][ns][key],
            })),
          } as FlattenedRow,
        ];
      }, [] as FlattenedRow[]),
      { ns, isUtil: true, isNS: false } as FlattenedRow,
    ];
  }, [] as FlattenedRow[]);
}

function TableCell({
  columnIndex,
  rowIndex,
  style,
  data,
}: GridChildComponentProps) {
  const item = data.flattened[rowIndex];

  const showAddNamespace = useAddNamespace();
  const showAddEntry = useAddEntry();

  const { canMutateLanguage, hasPermission } = useUser();

  if (item.isNS) {
    if (columnIndex === 0) {
      return (
        <UtilCol style={style}>
          {hasPermission(["NS_DELETE"]) && (
            <IconButton
              onClick={() => data.onNsDelete(item.ns)}
              small
              name="delete"
            />
          )}
        </UtilCol>
      );
    }
    if (columnIndex === 1) {
      return (
        <NsCell
          style={style}
          value={item.ns}
          onChange={(value) => data.onNsChange(item.ns, value)}
          readOnly={!hasPermission(["NS_MODIFY"])}
        />
      );
    } else {
      return <NSCol style={style} />;
    }
  }

  if (item.isUtil) {
    if (columnIndex === 0) {
      return <UtilCol style={style} />;
    }
    if (columnIndex === 1) {
      return (
        <Col
          style={{
            ...style,
            display: "flex",
            alignItems: "center",
            backgroundColor: "transparent",
          }}
        >
          {hasPermission(["ENTRY_CREATE"]) && (
            <Button
              type="color"
              iconBefore="add"
              text="Entry"
              onClick={() =>
                showAddEntry({
                  onCommit: (key) => data.onEntryAdd(item.ns, key),
                })
              }
              style={{ marginRight: 12 }}
            />
          )}

          {hasPermission(["NS_CREATE"]) && (
            <Button
              type="grey"
              iconBefore="add"
              text="Namespace"
              onClick={() =>
                showAddNamespace({
                  onCommit: (ns) => data.onNsAdd(ns, item.ns),
                })
              }
            />
          )}
        </Col>
      );
    } else {
      return <Col style={{ ...style, backgroundColor: "transparent" }} />;
    }
  }

  const { ns, key } = item;

  if (columnIndex === 0) {
    return (
      <UtilCol style={style}>
        {hasPermission(["ENTRY_DELETE"]) && (
          <IconButton
            onClick={() => data.onEntryDelete(ns, key)}
            small
            name="delete"
          />
        )}
      </UtilCol>
    );
  }

  if (columnIndex === 1) {
    return (
      <KeyCell
        readOnly={!hasPermission(["KEY_MODIFY"])}
        style={style}
        value={item.key}
        onChange={(value) => data.onKeyChange(item.ns, item.key, value)}
      />
    );
  }
  const { language, text } = item.values![columnIndex - 2];

  return (
    <Cell
      readOnly={
        !hasPermission(["ENTRY_MODIFY"]) || !canMutateLanguage(language)
      }
      style={style}
      showOnlyEmpty={data.showOnlyEmpty}
      key={`${ns}:${key}:${language}`}
      value={text || ""}
      onChange={(value) =>
        data.onEntryChange({ key: key as string, ns, language, value })
      }
      onFocus={() => {
        data.setEntryHasFocus(`${item.ns}.${item.key}`);
      }}
      onBlur={() => {
        data.setEntryHasFocus(null);
      }}
      onSave={(value) =>
        data.onEntryBlur({ key: key as string, ns, language, value })
      }
    />
  );
}

type Props = {
  translations: Record<string, any>;
  referenceLanguage: string;
  searchString: string;
  showOnlyEmpty: boolean;
  showNs: string;
  onEntryChange: (mutation: LanguageMutation) => void;
  onEntryBlur: (mutation: LanguageMutation) => void;
  onNsDelete: (ns: string) => void;
  onEntryDelete: (ns: string, key: string) => void;
  onNsAdd: (ns: string, afterNs: string) => void;
  onEntryAdd: (ns: string, key: string) => void;
  onNsChange: (oldNs: string, newKey: string) => void;
  onKeyChange: (ns: string, oldKey: string, newKey: string) => void;
};

function EditGrid({
  translations,
  referenceLanguage,
  searchString,
  showOnlyEmpty,
  showNs,
  onEntryChange,
  onEntryBlur,
  onNsDelete,
  onEntryDelete,
  onNsAdd,
  onEntryAdd,
  onNsChange,
  onKeyChange,
}: Props) {
  const [entryHasFocus, setEntryHasFocus] = React.useState<string | null>();

  const bodyRef = React.useRef<HTMLDivElement | null>(null);
  const topRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    function onScroll(evt: any) {
      if ((evt.target as HTMLElement).tagName.toLowerCase() === "textarea") {
        return;
      }
      topRef.current!.scrollLeft = evt.target.scrollLeft;
    }

    const body = bodyRef.current!;

    body.addEventListener("scroll", onScroll, true);

    return () => {
      body.removeEventListener("scroll", onScroll, true);
    };
  }, []);

  const flattened = flattenTranslations(translations, referenceLanguage)
    .filter((item, _, flattened) => {
      if (searchString === "") {
        return true;
      }

      if (item.isNS || item.isUtil) {
        const currentNS = item.ns;
        return (
          flattened.filter((item) => {
            if (item.isNS || item.isUtil || item.ns !== currentNS) {
              return false;
            }

            return (
              item.key!.toLocaleLowerCase().indexOf(searchString) > -1 ||
              item.values!.some(
                (x) =>
                  x.text !== null &&
                  x.text.toLowerCase().indexOf(searchString) > -1
              )
            );
          }).length > 0
        );
      }

      return (
        item.key!.toLocaleLowerCase().indexOf(searchString) > -1 ||
        item.values!.some(
          (x) =>
            x.text !== null && x.text.toLowerCase().indexOf(searchString) > -1
        )
      );
    })
    .filter((item) => {
      if (showNs !== "ALL" && item.ns !== showNs) {
        return false;
      }

      if (item.isNS || item.isUtil) {
        return true;
      }

      if (showOnlyEmpty) {
        if (
          `${item.ns}.${item.key}` === entryHasFocus ||
          item.values!.some((x) => !x.text || x.text.indexOf("@todo@") > -1)
        ) {
          return true;
        } else {
          return false;
        }
      }

      return true;
    });

  return (
    <Base>
      <Top ref={topRef}>
        <UtilCol style={{ width: UTIL_COL_WIDTH, minWidth: UTIL_COL_WIDTH }} />
        <HeaderCol />
        {Object.keys(translations).map((lang) => {
          return (
            <HeaderCol key={`header-${lang}`}>{lang.toUpperCase()}</HeaderCol>
          );
        })}
      </Top>
      <Body ref={bodyRef}>
        <AutoSizer>
          {({ width, height }) => (
            <VariableSizeGrid
              columnCount={Object.keys(translations).length + 2}
              columnWidth={(index) =>
                index === 0 ? UTIL_COL_WIDTH : COL_WIDTH
              }
              height={height}
              rowCount={flattened.length}
              width={width}
              rowHeight={() => 60}
              itemData={{
                flattened,
                onEntryChange,
                onEntryBlur,
                showOnlyEmpty,
                onNsDelete,
                onEntryDelete,
                onNsAdd,
                onEntryAdd,
                setEntryHasFocus,
                onNsChange,
                onKeyChange,
              }}
            >
              {TableCell}
            </VariableSizeGrid>
          )}
        </AutoSizer>
      </Body>
    </Base>
  );
}

export default EditGrid;
